import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Truck, Car, Sparkles, Wrench, PaintBucket, Check, ChevronRight, 
  Phone, MessageCircle, Calculator, Building2, Users, Shield,
  Clock, FileText, ArrowRight, Plus, Minus, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WHATSAPP_NUMBER = '27760784050';

const FLEET_FULL_VALET_INCLUDES = {
  exterior: [
    "Full exterior hand wash",
    "Wheels cleaned",
    "Wheel arches cleaned",
    "Tyres cleaned",
    "Tyre dressing",
    "Exterior windows cleaned",
    "Door jambs cleaned",
    "Exterior finishing inspection"
  ],
  interior: [
    "Full interior vacuum",
    "Seats cleaned",
    "Fabric seat extraction where required",
    "Leather seats cleaned where applicable",
    "Carpets deep cleaned",
    "Floor mats cleaned",
    "Dashboard cleaned",
    "Centre console cleaned",
    "Door panels cleaned",
    "Interior plastics and trim cleaned",
    "Interior windows and mirrors cleaned",
    "Boot/load area vacuumed",
    "Interior deodorising",
    "Final interior inspection"
  ]
};

const FLEET_NOT_INCLUDED = [
  "Machine polishing", "Paint correction", "Ceramic coating", "Engine bay detailing",
  "Major stain restoration", "Excessive pet-hair removal", "Biological contamination",
  "Adhesive removal", "Paint repairs", "Dent repairs", "Mechanical repairs"
];

const PARTNER_BENEFITS = [
  "Preferential contract pricing",
  "Priority booking",
  "Scheduled valet days",
  "Centralised fleet records",
  "Consolidated quotations",
  "Consolidated invoices where approved",
  "Vehicle service history",
  "Mechanical repair support",
  "Panel beating support",
  "Accident repair support",
  "Detailing services",
  "Pre-sale vehicle preparation"
];

export default function FleetServices() {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [enquiryType, setEnquiryType] = useState('booking');
  
  // Calculator state - simple vehicle count
  const [vehicleCount, setVehicleCount] = useState(5);
  const [calcResult, setCalcResult] = useState(null);
  const [calculating, setCalculating] = useState(false);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const response = await axios.get(`${API}/fleet/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Error fetching fleet config:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculatePrice = async () => {
    setCalculating(true);
    try {
      const response = await axios.post(`${API}/fleet/calculate`, { total_vehicles: vehicleCount });
      setCalcResult(response.data);
    } catch (error) {
      toast.error('Failed to calculate price');
    } finally {
      setCalculating(false);
    }
  };

  const openWhatsApp = (message) => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(message)}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-[#C41E3A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <main data-testid="fleet-services-page" className="min-h-screen bg-zinc-950">
      {/* Hero Section */}
      <section className="pt-24 pb-16 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-[#C41E3A]/10 to-transparent" />
        <div className="max-w-[1200px] mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-zinc-900 border border-zinc-800 px-4 py-2 rounded-full mb-6">
              <Truck className="w-4 h-4 text-[#C41E3A]" />
              <span className="text-xs text-zinc-400 uppercase tracking-wider">Fleet Services</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-white tracking-tight mb-4">
              MA AFRICA <span className="text-[#C41E3A]">FLEET CARE</span>
            </h1>
            
            <p className="text-lg md:text-xl text-zinc-300 max-w-2xl mx-auto mb-4">
              Professional vehicle care for businesses that need their fleet clean, presentable and road-ready.
            </p>
            
            <p className="text-sm text-zinc-500 max-w-xl mx-auto mb-8">
              From full valet services to mechanical repairs and panel beating, MA Africa provides businesses with one automotive partner for complete fleet care.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => { setEnquiryType('booking'); setShowEnquiryForm(true); }}
                className="bg-[#C41E3A] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#a01830] transition-colors inline-flex items-center justify-center gap-2"
                data-testid="fleet-booking-cta"
              >
                REQUEST FLEET BOOKING
                <ArrowRight className="w-5 h-5" />
              </button>
              <button
                onClick={() => { setEnquiryType('quote'); setShowEnquiryForm(true); }}
                className="bg-zinc-800 text-white px-8 py-4 rounded-xl font-semibold hover:bg-zinc-700 transition-colors"
                data-testid="fleet-quote-cta"
              >
                GET A FLEET QUOTE
              </button>
              <button
                onClick={() => openWhatsApp('Hi MA Africa, I need information about fleet services.')}
                className="bg-[#25D366] text-white px-6 py-4 rounded-xl font-semibold hover:bg-[#20bd5a] transition-colors inline-flex items-center justify-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                WhatsApp
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Fleet Full Valet Package */}
      <section className="py-16 px-6 bg-zinc-900/50">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                FLEET FULL VALET
              </h2>
              <p className="text-zinc-400 max-w-2xl mx-auto">
                A comprehensive interior and exterior valet designed specifically for businesses managing multiple vehicles.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
              {/* Exterior */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                    <Sparkles className="w-6 h-6 text-blue-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Exterior</h3>
                </div>
                <ul className="space-y-3">
                  {FLEET_FULL_VALET_INCLUDES.exterior.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-zinc-400">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Interior */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                    <Car className="w-6 h-6 text-purple-400" />
                  </div>
                  <h3 className="text-xl font-semibold text-white">Interior</h3>
                </div>
                <ul className="space-y-3">
                  {FLEET_FULL_VALET_INCLUDES.interior.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-zinc-400">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Not Included */}
            <div className="bg-zinc-900/50 border border-zinc-800 rounded-xl p-4">
              <p className="text-xs text-zinc-500 mb-2">Not included in standard Fleet Full Valet (available separately):</p>
              <div className="flex flex-wrap gap-2">
                {FLEET_NOT_INCLUDED.map((item, i) => (
                  <span key={i} className="text-xs text-zinc-600 bg-zinc-800 px-2 py-1 rounded">
                    {item}
                  </span>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <section className="py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Fleet Pricing
            </h2>
            <p className="text-zinc-400">
              Preferential rates based on fleet size. Minimum 5 vehicles.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-4 gap-6 mb-12">
            {config?.pricing_tiers?.map((tier, index) => (
              <motion.div
                key={tier.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`relative bg-zinc-900 border rounded-2xl p-6 text-center ${
                  tier.badge === 'BEST FOR LARGE FLEETS' 
                    ? 'border-[#C41E3A] ring-1 ring-[#C41E3A]/50' 
                    : 'border-zinc-800'
                }`}
              >
                {tier.badge && (
                  <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                    tier.badge === 'BEST FOR LARGE FLEETS' 
                      ? 'bg-[#C41E3A] text-white' 
                      : 'bg-zinc-800 text-zinc-400'
                  }`}>
                    {tier.badge}
                  </div>
                )}
                
                <h3 className="text-lg font-semibold text-white mb-2 mt-2">{tier.label}</h3>
                
                {tier.price_per_vehicle ? (
                  <>
                    <div className="text-4xl font-bold text-[#C41E3A] mb-1">
                      R{tier.price_per_vehicle}
                    </div>
                    <p className="text-sm text-zinc-500 mb-4">per vehicle</p>
                    
                    {/* Example pricing */}
                    <div className="text-xs text-zinc-600 space-y-1">
                      {tier.min_vehicles === 5 && (
                        <>
                          <p>5 vehicles = R{5 * tier.price_per_vehicle}</p>
                          <p>6 vehicles = R{6 * tier.price_per_vehicle}</p>
                          <p>7 vehicles = R{7 * tier.price_per_vehicle}</p>
                        </>
                      )}
                      {tier.min_vehicles === 8 && (
                        <p className="text-zinc-500">Save more with larger fleet</p>
                      )}
                      {tier.min_vehicles === 15 && (
                        <p className="text-zinc-500">High-volume fleet rate</p>
                      )}
                    </div>
                  </>
                ) : (
                  <>
                    <div className="text-2xl font-bold text-white mb-1">
                      Custom Rate
                    </div>
                    <p className="text-sm text-zinc-500 mb-4">Contract pricing</p>
                    <button
                      onClick={() => { setEnquiryType('quote'); setShowEnquiryForm(true); }}
                      className="text-sm text-[#C41E3A] hover:underline"
                    >
                      Request Contract Pricing
                    </button>
                  </>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Price Calculator */}
      <section className="py-16 px-6 bg-zinc-900/50">
        <div className="max-w-[600px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-8">
              <div className="inline-flex items-center gap-2 bg-zinc-800 px-4 py-2 rounded-full mb-4">
                <Calculator className="w-4 h-4 text-[#C41E3A]" />
                <span className="text-sm text-white font-medium">Fleet Price Calculator</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                Estimate Your Fleet Price
              </h2>
              <p className="text-sm text-zinc-500">
                Enter your fleet size to get an instant estimate
              </p>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              {/* Simple vehicle count input */}
              <div className="mb-6">
                <label className="block text-xs text-zinc-500 mb-3 text-center">How many vehicles?</label>
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={() => setVehicleCount(prev => Math.max(1, prev - 1))}
                    className="w-12 h-12 rounded-xl bg-zinc-800 text-zinc-400 hover:bg-zinc-700 flex items-center justify-center text-xl"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <input
                    type="number"
                    value={vehicleCount}
                    onChange={(e) => setVehicleCount(Math.max(1, parseInt(e.target.value) || 1))}
                    className="w-24 text-center text-3xl font-bold text-white bg-transparent border-b-2 border-zinc-700 focus:border-[#C41E3A] focus:outline-none"
                    min="1"
                  />
                  <button
                    onClick={() => setVehicleCount(prev => prev + 1)}
                    className="w-12 h-12 rounded-xl bg-zinc-800 text-zinc-400 hover:bg-zinc-700 flex items-center justify-center text-xl"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {vehicleCount < 5 && (
                <div className="flex items-start gap-2 text-amber-500 text-sm mb-4 justify-center">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>Fleet pricing applies to bookings of five or more vehicles.</span>
                </div>
              )}

              <button
                onClick={calculatePrice}
                disabled={vehicleCount < 5 || calculating}
                className="w-full bg-[#C41E3A] text-white py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {calculating ? 'Calculating...' : 'Calculate Estimate'}
              </button>

              {/* Result */}
              {calcResult && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-6 bg-zinc-800 rounded-xl p-5"
                >
                  {calcResult.eligible ? (
                    calcResult.requires_quote ? (
                      <div className="text-center">
                        <p className="text-white mb-2">For {calcResult.total_vehicles}+ vehicles</p>
                        <p className="text-zinc-400 text-sm mb-4">Contact us for custom contract pricing</p>
                        <button
                          onClick={() => { setEnquiryType('quote'); setShowEnquiryForm(true); }}
                          className="bg-[#C41E3A] text-white px-6 py-2 rounded-lg font-medium"
                        >
                          Request Quote
                        </button>
                      </div>
                    ) : (
                      <>
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-zinc-400">Pricing Tier:</span>
                          <span className="text-white font-medium">{calcResult.tier_label}</span>
                        </div>
                        
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-zinc-400">Rate per vehicle:</span>
                          <span className="text-white font-medium">R{calcResult.price_per_vehicle}</span>
                        </div>
                        
                        <div className="flex justify-between items-center mb-3">
                          <span className="text-zinc-400">Number of vehicles:</span>
                          <span className="text-white font-medium">{calcResult.total_vehicles}</span>
                        </div>
                        
                        <div className="border-t border-zinc-700 pt-3 mt-3">
                          <div className="flex justify-between items-center">
                            <span className="text-white font-semibold">Estimated Total:</span>
                            <span className="text-3xl font-bold text-[#C41E3A]">
                              R{calcResult.estimated_total?.toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </>
                    )
                  ) : (
                    <p className="text-center text-zinc-400">{calcResult.message}</p>
                  )}
                </motion.div>
              )}

              <p className="text-xs text-zinc-600 mt-4 text-center">
                Final pricing may change based on vehicle condition. Any additional charges must be approved before work begins.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Fleet Partner Programme */}
      <section className="py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="inline-flex items-center gap-2 bg-[#C41E3A]/10 border border-[#C41E3A]/30 px-4 py-2 rounded-full mb-6">
                  <Shield className="w-4 h-4 text-[#C41E3A]" />
                  <span className="text-xs text-[#C41E3A] uppercase tracking-wider font-semibold">Partnership Programme</span>
                </div>
                
                <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                  MA AFRICA <span className="text-[#C41E3A]">FLEET PARTNER</span>
                </h2>
                
                <p className="text-zinc-400 mb-6">
                  For businesses requiring ongoing vehicle care, MA Africa can create a scheduled fleet programme tailored to the size and needs of your fleet.
                </p>
                
                <button
                  onClick={() => { setEnquiryType('partner'); setShowEnquiryForm(true); }}
                  className="bg-[#C41E3A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors inline-flex items-center gap-2"
                >
                  BECOME A FLEET PARTNER
                  <ArrowRight className="w-5 h-5" />
                </button>
              </div>
              
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
                <h3 className="text-lg font-semibold text-white mb-4">Partner Benefits</h3>
                <ul className="grid grid-cols-2 gap-3">
                  {PARTNER_BENEFITS.map((benefit, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-zinc-400">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      {benefit}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Fleet Maintenance Valet */}
      <section className="py-16 px-6 bg-zinc-900/50">
        <div className="max-w-[1000px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-zinc-900 border border-zinc-800 rounded-2xl p-8"
          >
            <div className="flex items-start justify-between mb-6">
              <div>
                <span className="inline-block bg-blue-500/20 text-blue-400 text-xs font-semibold px-3 py-1 rounded-full mb-3">
                  FOR FLEET PARTNERS
                </span>
                <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
                  Fleet Maintenance Valet
                </h2>
                <p className="text-zinc-400 text-sm">
                  A recurring lighter cleaning package for vehicles already part of a fleet contract.
                </p>
              </div>
              <div className="text-right">
                <p className="text-sm text-zinc-500">Fleet Partner Pricing</p>
                <button
                  onClick={() => { setEnquiryType('partner'); setShowEnquiryForm(true); }}
                  className="text-[#C41E3A] text-sm font-medium hover:underline"
                >
                  Request Monthly Plan →
                </button>
              </div>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              <ul className="space-y-2">
                {[
                  "Exterior wash", "Wheels cleaned", "Tyres dressed", 
                  "Interior vacuum", "Mats cleaned", "Dashboard wipe-down"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                    <Check className="w-4 h-4 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
              <ul className="space-y-2">
                {[
                  "Console cleaning", "Interior trim wipe-down",
                  "Windows and mirrors", "Light interior refresh", "Final inspection"
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-2 text-sm text-zinc-400">
                    <Check className="w-4 h-4 text-green-500" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Complete Fleet Care - More Than Valet */}
      <section className="py-16 px-6">
        <div className="max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                MORE THAN A VALET
              </h2>
              <p className="text-zinc-400 max-w-2xl mx-auto">
                MA Africa can help manage more than the appearance of your vehicles. Our Fix & Detail model allows fleet owners to access multiple automotive services through one provider.
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-6">
              {/* Detail */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center mb-4">
                  <Sparkles className="w-6 h-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">DETAIL</h3>
                <ul className="space-y-2 mb-6">
                  {["Fleet Full Valet", "Maintenance Valet", "Interior restoration", "Pre-sale detailing", "Paint correction", "Headlight restoration"].map((item, i) => (
                    <li key={i} className="text-sm text-zinc-400 flex items-center gap-2">
                      <Check className="w-3 h-3 text-purple-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/detail"
                  className="text-purple-400 text-sm font-medium hover:underline inline-flex items-center gap-1"
                >
                  View Detailing Services <ChevronRight className="w-4 h-4" />
                </Link>
              </div>

              {/* Mechanical */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">MECHANICAL</h3>
                <ul className="space-y-2 mb-6">
                  {["Vehicle servicing", "Diagnostics", "Brake repairs", "Suspension repairs", "Cooling system repairs", "Battery diagnosis", "Fleet inspections"].map((item, i) => (
                    <li key={i} className="text-sm text-zinc-400 flex items-center gap-2">
                      <Check className="w-3 h-3 text-blue-400" />
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openWhatsApp('Hi MA Africa, I need mechanical fleet support.')}
                  className="text-blue-400 text-sm font-medium hover:underline inline-flex items-center gap-1"
                >
                  Request Fleet Support <ChevronRight className="w-4 h-4" />
                </button>
              </div>

              {/* Panel Beating */}
              <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 hover:border-zinc-700 transition-colors">
                <div className="w-12 h-12 rounded-xl bg-[#C41E3A]/20 flex items-center justify-center mb-4">
                  <PaintBucket className="w-6 h-6 text-[#C41E3A]" />
                </div>
                <h3 className="text-xl font-semibold text-white mb-4">PANEL BEATING</h3>
                <ul className="space-y-2 mb-6">
                  {["Accident damage", "Panel beating", "Dent repair", "Scratch repair", "Bumper repairs", "Spray painting", "Body restoration"].map((item, i) => (
                    <li key={i} className="text-sm text-zinc-400 flex items-center gap-2">
                      <Check className="w-3 h-3 text-[#C41E3A]" />
                      {item}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => openWhatsApp('Hi MA Africa, I need body repair for my fleet.')}
                  className="text-[#C41E3A] text-sm font-medium hover:underline inline-flex items-center gap-1"
                >
                  Request Body Repair <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Business Positioning */}
      <section className="py-16 px-6 bg-gradient-to-b from-zinc-900/50 to-zinc-950">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              YOUR FLEET. <span className="text-[#C41E3A]">ONE AUTOMOTIVE PARTNER.</span>
            </h2>
            <p className="text-lg text-zinc-400 mb-8">
              Detailing. Mechanical. Panel Beating. Complete Fleet Care.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="bg-zinc-800 px-4 py-2 rounded-full text-sm text-zinc-300">CLEAN</span>
              <span className="text-zinc-600">→</span>
              <span className="bg-zinc-800 px-4 py-2 rounded-full text-sm text-zinc-300">SERVICE</span>
              <span className="text-zinc-600">→</span>
              <span className="bg-zinc-800 px-4 py-2 rounded-full text-sm text-zinc-300">REPAIR</span>
              <span className="text-zinc-600">→</span>
              <span className="bg-zinc-800 px-4 py-2 rounded-full text-sm text-zinc-300">RESTORE</span>
              <span className="text-zinc-600">→</span>
              <span className="bg-[#C41E3A] px-4 py-2 rounded-full text-sm text-white font-medium">ROAD READY</span>
            </div>

            <button
              onClick={() => { setEnquiryType('booking'); setShowEnquiryForm(true); }}
              className="bg-[#C41E3A] text-white px-8 py-4 rounded-xl font-semibold hover:bg-[#a01830] transition-colors inline-flex items-center gap-2"
            >
              BOOK FLEET VALET
              <ArrowRight className="w-5 h-5" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Terms */}
      <section className="py-8 px-6 border-t border-zinc-900">
        <div className="max-w-[1000px] mx-auto">
          <h3 className="text-sm font-semibold text-zinc-400 mb-3">Fleet Pricing Terms</h3>
          <p className="text-xs text-zinc-600 leading-relaxed">
            Fleet pricing is available for bookings of five or more vehicles. Published fleet rates apply to vehicles in normal serviceable condition. Larger vehicles and vehicles requiring excessive stain removal, pet-hair removal, mud removal, biological cleaning or restoration may incur additional charges. Any additional work or charges will be communicated for approval before being performed. Mechanical repairs, panel beating, paint repairs and specialist detailing treatments are quoted separately unless included in a specific fleet agreement.
          </p>
        </div>
      </section>

      {/* Fleet Enquiry Form Modal */}
      {showEnquiryForm && (
        <FleetEnquiryForm
          type={enquiryType}
          config={config}
          onClose={() => setShowEnquiryForm(false)}
        />
      )}
    </main>
  );
}

// Fleet Enquiry Form Component
function FleetEnquiryForm({ type, config, onClose }) {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState('');
  
  const [formData, setFormData] = useState({
    company_name: '',
    registration_number: '',
    vat_number: '',
    contact_person: '',
    cell_number: '',
    whatsapp_number: '',
    email: '',
    vehicle_count: 5,
    vehicle_details: '',
    frequency: 'once_off',
    service_location: 'workshop',
    condition: 'regular',
    condition_notes: '',
    photos: [],
    preferred_start_date: '',
    notes: '',
    enquiry_type: type
  });

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const updateVehicleCount = (count) => {
    setFormData(prev => ({ ...prev, vehicle_count: Math.max(1, count) }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/fleet/enquiry`, formData);
      
      if (response.data.success) {
        setSuccess(true);
        setReferenceNumber(response.data.reference_number);
        toast.success('Fleet enquiry submitted successfully!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit enquiry');
    } finally {
      setSubmitting(false);
    }
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.company_name && formData.contact_person && formData.cell_number && formData.email;
    }
    if (step === 2) {
      return formData.vehicle_count >= 5;
    }
    return true;
  };

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-zinc-900 rounded-2xl p-8 max-w-md w-full text-center border border-zinc-800"
        >
          <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-500" />
          </div>
          <h3 className="text-2xl font-bold text-white mb-2">Enquiry Submitted!</h3>
          <p className="text-zinc-400 mb-4">
            Your fleet {type === 'quote' ? 'quote request' : type === 'partner' ? 'partnership enquiry' : 'booking request'} has been received.
          </p>
          <div className="bg-zinc-800 rounded-xl p-4 mb-6">
            <p className="text-xs text-zinc-500 mb-1">Reference Number</p>
            <p className="text-lg font-mono font-bold text-[#C41E3A]">{referenceNumber}</p>
          </div>
          <p className="text-sm text-zinc-500 mb-6">
            We&apos;ll contact you shortly to discuss your fleet requirements.
          </p>
          <button
            onClick={onClose}
            className="w-full bg-[#C41E3A] text-white py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors"
          >
            Done
          </button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-zinc-900 rounded-2xl max-w-2xl w-full border border-zinc-800 my-8"
      >
        {/* Header */}
        <div className="p-6 border-b border-zinc-800">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-xl font-bold text-white">
                {type === 'quote' ? 'Request Fleet Quote' : type === 'partner' ? 'Become a Fleet Partner' : 'Book Fleet Valet'}
              </h3>
              <p className="text-sm text-zinc-500">Step {step} of 3</p>
            </div>
            <button onClick={onClose} className="text-zinc-500 hover:text-white">
              ✕
            </button>
          </div>
          {/* Progress */}
          <div className="flex gap-2 mt-4">
            {[1, 2, 3].map(s => (
              <div
                key={s}
                className={`flex-1 h-1 rounded-full ${s <= step ? 'bg-[#C41E3A]' : 'bg-zinc-800'}`}
              />
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {step === 1 && (
            <div className="space-y-4">
              <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Company Information</h4>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Company Name *</label>
                <input
                  type="text"
                  value={formData.company_name}
                  onChange={(e) => handleChange('company_name', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                  placeholder="Your Company Name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Registration Number</label>
                  <input
                    type="text"
                    value={formData.registration_number}
                    onChange={(e) => handleChange('registration_number', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">VAT Number</label>
                  <input
                    type="text"
                    value={formData.vat_number}
                    onChange={(e) => handleChange('vat_number', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                    placeholder="Optional"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Contact Person *</label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => handleChange('contact_person', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                  placeholder="Full Name"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Cell Number *</label>
                  <input
                    type="tel"
                    value={formData.cell_number}
                    onChange={(e) => handleChange('cell_number', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                    placeholder="076 123 4567"
                  />
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">WhatsApp Number</label>
                  <input
                    type="tel"
                    value={formData.whatsapp_number}
                    onChange={(e) => handleChange('whatsapp_number', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                    placeholder="Same as cell if blank"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                  placeholder="you@company.com"
                />
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6">
              <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Fleet Information</h4>
              
              {/* Simple vehicle count input */}
              <div>
                <label className="block text-xs text-zinc-500 mb-3">Number of Vehicles *</label>
                <div className="flex items-center justify-center gap-4 bg-zinc-800 rounded-xl p-4">
                  <button
                    onClick={() => updateVehicleCount(formData.vehicle_count - 1)}
                    className="w-12 h-12 rounded-xl bg-zinc-700 text-zinc-400 hover:bg-zinc-600 flex items-center justify-center text-xl"
                  >
                    <Minus className="w-5 h-5" />
                  </button>
                  <input
                    type="number"
                    value={formData.vehicle_count}
                    onChange={(e) => updateVehicleCount(parseInt(e.target.value) || 1)}
                    className="w-24 text-center text-3xl font-bold text-white bg-transparent border-b-2 border-zinc-600 focus:border-[#C41E3A] focus:outline-none"
                    min="1"
                  />
                  <button
                    onClick={() => updateVehicleCount(formData.vehicle_count + 1)}
                    className="w-12 h-12 rounded-xl bg-zinc-700 text-zinc-400 hover:bg-zinc-600 flex items-center justify-center text-xl"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                {formData.vehicle_count < 5 && (
                  <p className="text-xs text-amber-500 mt-2 text-center">Minimum 5 vehicles required for fleet pricing.</p>
                )}
              </div>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Vehicle Makes/Models (Optional)</label>
                <textarea
                  value={formData.vehicle_details}
                  onChange={(e) => handleChange('vehicle_details', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none resize-none"
                  rows={2}
                  placeholder="e.g. 5x Toyota Corolla, 2x Ford Ranger"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Service Frequency *</label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => handleChange('frequency', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                  >
                    {config?.frequency_options?.map(opt => (
                      <option key={opt.id} value={opt.id}>{opt.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-zinc-500 mb-1">Service Location *</label>
                  <select
                    value={formData.service_location}
                    onChange={(e) => handleChange('service_location', e.target.value)}
                    className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                  >
                    <option value="workshop">MA Africa Workshop</option>
                    <option value="client_premises">Client Premises</option>
                    <option value="to_discuss">To Be Discussed</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <h4 className="text-sm font-semibold text-zinc-400 uppercase tracking-wider mb-4">Vehicle Condition & Schedule</h4>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-2">General Vehicle Condition *</label>
                <div className="grid grid-cols-2 gap-2">
                  {config?.condition_options?.map(opt => (
                    <button
                      key={opt.id}
                      onClick={() => handleChange('condition', opt.id)}
                      className={`p-3 rounded-xl text-sm text-left transition-all ${
                        formData.condition === opt.id
                          ? 'bg-[#C41E3A] text-white'
                          : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                      }`}
                    >
                      {opt.label}
                      {opt.flag && <span className="text-xs opacity-70 block">Assessment may be required</span>}
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Condition Notes (Optional)</label>
                <textarea
                  value={formData.condition_notes}
                  onChange={(e) => handleChange('condition_notes', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none resize-none"
                  rows={2}
                  placeholder="Any specific concerns about vehicle condition"
                />
              </div>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Preferred Start Date</label>
                <input
                  type="date"
                  value={formData.preferred_start_date}
                  onChange={(e) => handleChange('preferred_start_date', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none"
                />
              </div>
              
              <div>
                <label className="block text-xs text-zinc-500 mb-1">Additional Notes</label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => handleChange('notes', e.target.value)}
                  className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 rounded-xl text-white focus:border-[#C41E3A] focus:outline-none resize-none"
                  rows={3}
                  placeholder="Any other requirements or questions"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-6 border-t border-zinc-800 flex justify-between">
          {step > 1 ? (
            <button
              onClick={() => setStep(s => s - 1)}
              className="px-6 py-3 text-zinc-400 hover:text-white transition-colors"
            >
              Back
            </button>
          ) : (
            <button
              onClick={onClose}
              className="px-6 py-3 text-zinc-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
          )}
          
          {step < 3 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={!canProceed()}
              className="bg-[#C41E3A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center gap-2"
            >
              Continue
              <ChevronRight className="w-5 h-5" />
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={submitting}
              className="bg-[#C41E3A] text-white px-6 py-3 rounded-xl font-semibold hover:bg-[#a01830] transition-colors disabled:opacity-50 inline-flex items-center gap-2"
            >
              {submitting ? 'Submitting...' : 'Submit Enquiry'}
              <ArrowRight className="w-5 h-5" />
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
}
